import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AlertCircle,
    MapPin,
    Plane,
    Utensils,
    Car,
    CreditCard,
    Train,
    Camera,
    Search,
    ChevronRight,
    TrendingUp,
    Calendar,
    ShoppingBag,
    Navigation,
    Home,
    Star,
    Wallet,
    Truck
} from 'lucide-react';
import { useUser } from '@clerk/react';
import AdCarousel from '../components/AdCarousel';
import { allSponsoredAds } from '../data/mockData';

const HomeView = () => {
    const navigate = useNavigate();
    const { isLoaded, isSignedIn, user } = useUser();
    
    const displayId = user?.id || 'Visitor';
    const displayName = user?.firstName || 'Guest';

    const essentialServices = [
        { icon: <Car color="#006838" />, label: 'Airport', path: '/marketplace?cat=Drivers' },
        { icon: <CreditCard color="#006838" />, label: 'Cowry', path: '/wallet' },
        { icon: <Wallet color="#006838" />, label: 'Wallet', path: '/wallet' },
        { icon: <Truck color="#006838" />, label: 'Logistics', path: '/marketplace?cat=Logistics' },
    ];

    const exploreLagos = [
        { icon: <Utensils color="#006838" />, label: 'Food', path: '/explore?tab=food' },
        { icon: <Calendar color="#006838" />, label: 'Events', path: '/explore' },
        { icon: <MapPin color="#006838" />, label: 'Guides', path: '/explore' },
        { icon: <ShoppingBag color="#006838" />, label: 'Shopping', path: '/marketplace' },
    ];

    const travelUtilities = [
        { icon: <Train color="#006838" />, label: 'Trains', path: '/wallet?tab=train' },
        { icon: <Navigation color="#006838" />, label: 'Taxi', path: '/marketplace?cat=Drivers' },
        { icon: <Home color="#006838" />, label: 'Hotels', path: '/explore?tab=stay' },
    ];

    const randomAds = [...allSponsoredAds].sort(() => 0.5 - Math.random()).slice(0, 3);

    return (
        <div className="home-view animate-fade">
            <section className="welcome-banner">
                <div className="banner-overlay"></div>
                <div className="user-intro">
                    <h1>Welcome, {displayName}! 👋</h1>
                    <p>Lagos Visitor Companion | ID: <span style={{ opacity: 0.7, fontSize: '0.8em' }}>{displayId}</span></p>
                    <div className="city-status">
                        <span className="status-tag">📍 Victoria Island</span>
                        <span className="status-tag">🌤 29°C | Humid</span>
                        <span className="status-tag">💱 $1 = ₦1,550</span>
                    </div>
                </div>

                <div className="search-bar">
                    <Search size={18} color="rgba(0,0,0,0.5)" />
                    <input type="text" placeholder="Search experiences, guides, services..." />
                </div>
            </section>

            <AdCarousel providers={randomAds} title="Featured Providers" />

            <section className="popular-services-sec card animate-fade">
                <div className="sec-header">
                    <h3 style={{ fontSize: '16px', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Star size={18} fill="var(--gold-verified)" color="var(--gold-verified)" /> Popular Services
                    </h3>
                    <span className="social-proof-badge">Booked 50+ times today</span>
                </div>
                <div className="popular-list">
                    <div className="pop-item" onClick={() => navigate('/explore')}>
                        <div className="pop-info">
                            <h4>Lagos Island Food Tour</h4>
                            <p>Highly rated by 120+ visitors</p>
                        </div>
                        <TrendingUp size={16} color="var(--primary-green)" />
                    </div>
                    <div className="pop-item" onClick={() => navigate('/marketplace?cat=Drivers')}>
                        <div className="pop-info">
                            <h4>Private Security Driver</h4>
                            <p>Trending for Airport Pickups</p>
                        </div>
                        <TrendingUp size={16} color="var(--primary-green)" />
                    </div>
                    <div className="pop-item" onClick={() => navigate('/explore?tab=stay')}>
                        <div className="pop-info">
                            <h4>Lekki Beach Day Trip</h4>
                            <p>Selling fast for the weekend</p>
                        </div>
                        <TrendingUp size={16} color="var(--primary-green)" />
                    </div>
                </div>
            </section>

            <div className="services-container">
                <section className="tier-section">
                    <h3>Essential Services</h3>
                    <div className="quick-grid-v2">
                        {essentialServices.map((action, i) => (
                            <div key={i} className="action-item" onClick={() => navigate(action.path)}>
                                <div className="icon-circle">{action.icon}</div>
                                <span>{action.label}</span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="tier-section">
                    <h3>Explore Lagos</h3>
                    <div className="quick-grid-v2">
                        {exploreLagos.map((action, i) => (
                            <div key={i} className="action-item" onClick={() => navigate(action.path)}>
                                <div className="icon-circle">{action.icon}</div>
                                <span>{action.label}</span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="tier-section">
                    <h3>Travel Utilities</h3>
                    <div className="quick-grid-v2 travel-grid">
                        {travelUtilities.map((action, i) => (
                            <div key={i} className="action-item" onClick={() => navigate(action.path)}>
                                <div className="icon-circle">{action.icon}</div>
                                <span>{action.label}</span>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <section className="discover-lagos">
                <h3>Discover Lagos</h3>
                <div className="discover-scroll">
                    <div className="discover-card beaches" onClick={() => navigate('/explore')}>
                        <h4>🌴 Beaches</h4>
                    </div>
                    <div className="discover-card art" onClick={() => navigate('/explore')}>
                        <h4>🎨 Art & Culture</h4>
                    </div>
                    <div className="discover-card food" onClick={() => navigate('/explore?tab=food')}>
                        <h4>🍲 Nigerian Cuisine</h4>
                    </div>
                    <div className="discover-card nightlife" onClick={() => navigate('/explore')}>
                        <h4>🕺 Nightlife</h4>
                    </div>
                </div>
            </section>

            <section className="emergency-quick">
                <div className="emergency-banner" onClick={() => navigate('/emergency')}>
                    <AlertCircle color="white" size={24} />
                    <div className="em-text">
                        <h4>Emergency Assistance</h4>
                        <p>Police, Ambulance & Fire Service</p>
                    </div>
                    <ChevronRight color="white" />
                </div>
            </section>

            <section className="featured-cards">
                <div className="h-card guidance" onClick={() => navigate('/explore')}>
                    <div className="h-card-content">
                        <h4>Lagos Survival Guide</h4>
                        <p>Safety, Money & Transport</p>
                    </div>
                </div>
                <div className="h-card provider-join" onClick={() => navigate('/provider-setup')}>
                    <div className="h-card-content">
                        <h4 style={{ fontSize: '18px', fontWeight: '800' }}>Earn with EZ Naija</h4>
                        <p style={{ fontSize: '12px' }}>Become a Verified Provider</p>
                    </div>
                </div>
            </section>

            <style dangerouslySetInnerHTML={{
                __html: `
        .home-view { display: flex; flex-direction: column; gap: 24px; padding-bottom: 30px; }
        
        .welcome-banner { position: relative; padding: 40px 20px 30px; border-radius: 0 0 24px 24px; margin: -20px -20px 0; background-image: url('https://images.unsplash.com/photo-1618828665011-0abd973f7bb8?auto=format&fit=crop&q=80&w=800'); background-size: cover; background-position: center; z-index: 1; }
        .banner-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(180deg, rgba(0, 104, 56, 0.4) 0%, rgba(0, 0, 0, 0.8) 100%); border-radius: 0 0 24px 24px; z-index: -1; }

        .user-intro h1 { font-size: 36px; font-weight: 800; margin-bottom: 8px; color: white; letter-spacing: -1px; position: relative; }
        .user-intro p { font-size: 16px; color: rgba(255,255,255,0.9); margin-bottom: 24px; position: relative; font-weight: 500; }
        .city-status { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 24px; position: relative; display: none; }
        .status-tag { background: rgba(255, 255, 255, 0.2); backdrop-filter: blur(10px); padding: 6px 14px; border-radius: 20px; font-size: 13px; font-weight: 700; color: white; border: 1px solid rgba(255,255,255,0.3); }
        
        .search-bar { background: white; padding: 18px 24px; border-radius: 30px; display: flex; align-items: center; gap: 12px; box-shadow: 0 20px 40px rgba(0,0,0,0.2); border: none; position: relative; margin-top: 20px; transform: translateY(20px); width: 90%; margin-left: auto; margin-right: auto; }
        .search-bar input { border: none; background: none; outline: none; width: 100%; font-size: 15px; font-weight: 500; color: var(--text-dark); }
        .search-bar input::placeholder { color: #999; font-weight: 400; }

        .services-container { display: flex; flex-direction: column; gap: 28px; padding: 0 10px; }
        .tier-section h3 { font-size: 16px; color: var(--text-dark); margin-bottom: 12px; font-weight: 800; border-left: 3px solid var(--primary-green); padding-left: 8px; }

        .quick-grid-v2 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
        .travel-grid { grid-template-columns: repeat(3, 1fr); }
        .action-item { display: flex; flex-direction: column; align-items: center; gap: 8px; cursor: pointer; }
        .icon-circle { width: 50px; height: 50px; background: #f0f7f3; border: 1.5px solid var(--primary-green); border-radius: 16px; display: flex; align-items: center; justify-content: center; transition: 0.2s; box-shadow: 0 2px 6px rgba(0,0,0,0.02); }
        .action-item:hover .icon-circle { transform: scale(1.05); background: var(--light-green); border-color: var(--primary-green); }
        .action-item span { font-size: 10.5px; font-weight: 700; color: var(--text-dark); text-align: center; }

        .emergency-banner { background: var(--emergency-red); border-radius: var(--radius-md); padding: 16px; display: flex; align-items: center; gap: 16px; cursor: pointer; color: white; margin: 0 10px; }
        .em-text { flex: 1; }
        .em-text h4 { color: white; margin: 0; font-size: 15px; }
        .em-text p { color: rgba(255,255,255,0.8); font-size: 12px; margin: 0; }

        .featured-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding: 0 10px; }
        .h-card { height: 120px; border-radius: var(--radius-md); padding: 16px; display: flex; align-items: flex-end; cursor: pointer; background-size: cover; background-position: center; position: relative; overflow: hidden; }
        .h-card::after { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(0deg, rgba(0,0,0,0.6) 0%, transparent 100%); }
        .h-card-content { position: relative; z-index: 1; }
        .h-card-content h4 { color: white; font-size: 14px; margin-bottom: 2px; }
        .h-card-content p { color: rgba(255,255,255,0.8); font-size: 11px; margin: 0; }
        
        .popular-services-sec { margin: 0 10px 24px; border: 1px solid var(--border-gray); background: white; padding: 16px; border-radius: var(--radius-md); position: relative; z-index: 10; }
        .popular-services-sec .sec-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; border-bottom: 1px solid #f0f0f0; padding-bottom: 12px; }
        .social-proof-badge { font-size: 10px; color: var(--primary-green); font-weight: 800; background: var(--light-green); padding: 4px 8px; border-radius: 12px; }
        .popular-list { display: flex; flex-direction: column; gap: 12px; }
        .pop-item { display: flex; justify-content: space-between; align-items: center; cursor: pointer; padding: 10px; border-radius: 8px; transition: 0.2s; background: #fdfdfd; border: 1px solid #f0f0f0; }
        .pop-item:active { transform: scale(0.98); background: var(--light-green); border-color: rgba(0, 104, 56, 0.2); }
        .pop-info h4 { font-size: 14px; margin-bottom: 4px; color: var(--text-dark); font-weight: 700; }
        .pop-info p { font-size: 11px; color: var(--text-light); margin: 0; font-style: italic; }

        .discover-lagos { padding: 0 10px; margin-bottom: 8px; }
        .discover-lagos h3 { font-size: 18px; margin-bottom: 12px; color: var(--text-dark); font-weight: 800; }
        .discover-scroll { display: flex; gap: 12px; overflow-x: auto; padding-bottom: 8px; margin: 0 -10px; padding-left: 10px; padding-right: 10px; scrollbar-width: none; }
        .discover-scroll::-webkit-scrollbar { display: none; }
        .discover-card { min-width: 140px; height: 160px; border-radius: 16px; display: flex; align-items: flex-end; padding: 16px; background-size: cover; background-position: center; position: relative; overflow: hidden; box-shadow: 0 4px 10px rgba(0,0,0,0.1); cursor: pointer; transition: 0.2s; }
        .discover-card:active { transform: scale(0.96); }
        .discover-card::after { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.8) 100%); }
        .discover-card h4 { position: relative; z-index: 1; color: white; font-size: 14px; margin: 0; font-weight: 700; }
        
        .beaches { background-image: url('https://images.unsplash.com/photo-1543884849-0111f1810ea3?auto=format&fit=crop&q=80&w=400'); }
        .art { background-image: url('https://images.unsplash.com/photo-1590059536241-11910609335a?auto=format&fit=crop&q=80&w=400'); }
        .food { background-image: url('https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&q=80&w=400'); }
        .nightlife { background-image: url('https://images.unsplash.com/photo-1566737236500-c8ac43014a67?auto=format&fit=crop&q=80&w=400'); }

        .guidance { background-image: url('https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=400'); }
        .provider-join { background-image: url('https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=400'); }
      `}} />
        </div>
    );
};

export default HomeView;
